import type { PracticeComparison } from '@engineer-os/engine';
import type { Progress } from '../progress';
import { fetchFeedback, fetchMeter } from './efficiency';

export const FREE_PREVIEW_IDS = ['l00-hello-terminal', 'l02-first-snapshot', 'l13-make-the-tests-pass', 'l14-read-the-stack-trace', 'l17-who-broke-it'] as const;
export function previewCompleted(progress: Progress): boolean {
  return FREE_PREVIEW_IDS.every((id) => progress.levels[id]?.completed === true);
}
export interface PreviewMeasurement { beforeMicro: number; afterMicro: number | null; book: string; reported: boolean }

/** Require all five complete, server-produced histories; never scale a lab ratio. */
export function summarizePreviewComparisons(rows: { ref: string; comparison: PracticeComparison }[]): PreviewMeasurement | null {
  if (rows.length !== FREE_PREVIEW_IDS.length || new Set(rows.map((r) => r.ref)).size !== rows.length) return null;
  if (!FREE_PREVIEW_IDS.every((id) => rows.some((r) => r.ref === id))) return null;
  const first = rows[0]!.comparison;
  if (rows.some(({ comparison: c }) => c.book !== first.book || c.reported !== first.reported || !Number.isSafeInteger(c.before.costMicro) || c.before.costMicro <= 0 || (c.after && (!Number.isSafeInteger(c.after.costMicro) || c.after.costMicro < 0)))) return null;
  const beforeMicro = rows.reduce((sum, r) => sum + r.comparison.before.costMicro, 0);
  const afterMicro = rows.every((r) => r.comparison.after !== null) ? rows.reduce((sum, r) => sum + r.comparison.after!.costMicro, 0) : null;
  if (!Number.isSafeInteger(beforeMicro) || (afterMicro !== null && !Number.isSafeInteger(afterMicro))) return null;
  return { beforeMicro, afterMicro, book: first.book, reported: first.reported };
}

export async function fetchPreviewMeasurement(): Promise<PreviewMeasurement | null> {
  const meter = await fetchMeter();
  if (!meter?.shown) return null;
  // The recent ledger locates IDs only. Comparisons come from each private
  // feedback endpoint's full bounded history, never a truncated client sum.
  const latest = FREE_PREVIEW_IDS.map((ref) => meter.attempts.find((a) => a.kind === 'level' && a.ref === ref));
  if (latest.some((a) => !a || a.outcome !== 'passed' || a.by !== 'agent')) return null;
  const feedback = await Promise.all(latest.map((a) => fetchFeedback(a!.id)));
  const check = await fetchMeter();
  if (!check?.shown || FREE_PREVIEW_IDS.some((ref, i) => check.attempts.find((a) => a.kind === 'level' && a.ref === ref)?.id !== latest[i]!.id)) return null;
  const rows: { ref: string; comparison: PracticeComparison }[] = [];
  for (let i = 0; i < feedback.length; i++) {
    const f = feedback[i];
    if (!f?.practice || f.attempt.id !== latest[i]!.id || f.attempt.ref !== FREE_PREVIEW_IDS[i]) return null;
    rows.push({ ref: f.attempt.ref, comparison: f.practice });
  }
  return summarizePreviewComparisons(rows);
}
